import { CalendarIcon } from 'lucide-react'
import moment from 'moment'
import React, { useState } from 'react'
import { cn } from '../utils/utils'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Button } from './button'
import { Calendar } from './calendar'
import { Label } from './Label'
import { InputError } from './InputError'

function isValidDate(date: moment.Moment | undefined) {
    return !!date && date.isValid()
}

function parseInputDate(value: string) {
    // Parse as DD-MM-YYYY
    const date = moment(value, 'DD-MM-YYYY', true)
    return isValidDate(date) ? date : undefined
}

function autoFormatDateInput(value: string) {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '').slice(0, 8)
    let formatted = digits
    if (digits.length > 4) {
        formatted = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`
    } else if (digits.length > 2) {
        formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`
    }
    return formatted
}

type LabelDatePickerProps = {
    label?: string
    value: Date | undefined
    onChange: (date: Date | undefined) => void
    error?: string
    required?: boolean
    minDate?: Date
    maxDate?: Date
    id?: string
    className?: string
    placeholder?: string
    isVertical?: boolean
    prefixIcon?: React.ReactNode
}

export const LabelDatePicker = React.forwardRef<HTMLInputElement, LabelDatePickerProps>(
    (
        {
            label,
            value,
            onChange,
            error,
            required = false,
            minDate,
            maxDate,
            id,
            className = '',
            placeholder = 'DD-MM-YYYY',
            isVertical = true,
            prefixIcon,
            ...props
        },
        ref,
    ) => {
        const [open, setOpen] = useState(false)
        const [inputValue, setInputValue] = useState(value ? moment(value).format('DD-MM-YYYY') : '')

        // Keep input in sync with value prop (e.g. when calendar is used)
        React.useEffect(() => {
            const formatted = value ? moment(value).format('DD-MM-YYYY') : ''
            if (formatted !== inputValue) setInputValue(formatted)
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [value])

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value
            const formatted = autoFormatDateInput(raw)
            setInputValue(formatted)

            // Only call onChange if the input is a valid, full date
            if (formatted.length === 10) {
                const parsed = parseInputDate(formatted)
                if (isValidDate(parsed)) {
                    // Set time to noon to avoid timezone issues
                    const safeDate = parsed!.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).toDate()
                    onChange(safeDate)
                    return
                }
            }
            // If cleared, propagate undefined
            if (!formatted) {
                onChange(undefined)
            }
        }

        const handleCalendarSelect = (date: Date | undefined) => {
            if (date) {
                const m = moment(date)
                setInputValue(m.format('DD-MM-YYYY'))
                // Set time to noon to avoid timezone issues
                const safeDate = m.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).toDate()
                onChange(safeDate)
            }
            setOpen(false)
        }

        const input = (
            <div className="relative w-full">
                {prefixIcon && (
                    <div className="pointer-events-none absolute inset-y-0 -left-7 flex items-center pl-3">
                        {prefixIcon}
                    </div>
                )}
                <input
                    ref={ref}
                    id={id}
                    value={inputValue}
                    placeholder={placeholder}
                    className={cn(
                        'bg-background pr-10 w-full rounded-full border border-gray-300 py-2 px-4 focus:outline-none focus:border-[#16a34a] focus:ring-1 focus:ring-[#16a34a]',
                        error && 'border-red-500',
                        prefixIcon && 'pl-7',
                    )}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                        if (!e.target.value) onChange(undefined)
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                            e.preventDefault()
                            setOpen(true)
                        }
                    }}
                    required={required}
                    maxLength={10}
                    inputMode="numeric"
                    pattern="\d{2}-\d{2}-\d{4}"
                    aria-invalid={!!error}
                    aria-describedby={error ? `${id}-error` : undefined}
                    {...props}
                />
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            className="absolute top-1/2 right-2 size-6 -translate-y-1/2 py-0 border-0"
                            tabIndex={-1}
                            aria-label="Open calendar"
                        >
                            <CalendarIcon className="h-4 w-4" color="#737671" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto overflow-hidden p-0" align="end" alignOffset={-8} sideOffset={10}>
                        <Calendar
                            mode="single"
                            selected={value}
                            captionLayout="dropdown"
                            onSelect={handleCalendarSelect}
                            disabled={(date) =>
                                (minDate && date < minDate) || (maxDate && date > maxDate) || date > new Date()
                            }
                        />
                    </PopoverContent>
                </Popover>
            </div>
        )

        return (
            <div
                className={`flex ${isVertical ? 'flex-col' : 'flex-row justify-between items-center'} w-full ${className}`}
            >
                {label && (
                    <Label className="text-sm" htmlFor={id}>
                        {label}
                    </Label>
                )}
                {input}
                <InputError message={error} />
            </div>
        )
    },
)
